import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Plus, Search, Edit, Trash2, Mail, Phone, X } from 'lucide-react';
import { createInvite } from '../../services/api';
import EmailPreviewModal from '../shared/EmailPreviewModal';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
const input = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
const btn = { padding: '0.75rem 1.5rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' };
const actionBtn = { width: '2.25rem', height: '2.25rem', minWidth: '2.25rem', minHeight: '2.25rem', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(148,163,184,0.1)', borderRadius: '0.5rem', border: 'none', color: '#94a3b8', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 };

export default function Clients() {
  const { clients, invoices, fetchClients, fetchInvoices, addClient, editClient, removeClient } = useApp();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', address: '' });

  // Invite modal state
  const [showInvite, setShowInvite] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewClient, setPreviewClient] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => { fetchClients(); fetchInvoices(); }, [fetchClients, fetchInvoices]);

  const filtered = clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.company?.toLowerCase().includes(search.toLowerCase()));

  const openModal = (client = null) => {
    setEditing(client);
    setForm(client || { name: '', email: '', phone: '', company: '', address: '' });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email) return alert('Name and email required');
    try {
      if (editing) await editClient(editing._id, form);
      else await addClient(form);
      setShowModal(false);
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this client?')) {
      try { await removeClient(id); } catch (err) { alert('Error'); }
    }
  };

  const handleInviteClick = (client) => {
    setPreviewClient(client);
    setShowPreview(true);
  };

  const handleConfirmInvite = async () => {
    if (!previewClient || !previewClient._id) return;
    setInviteLoading(true);
    try {
      const r = await createInvite(previewClient._id, 168);
      console.log('createInvite response', r);
      setInviteData({ client: previewClient, token: r.inviteToken, expiresAt: r.expiresAt });
      setShowPreview(false);
      setShowInvite(true);

      if (r && r.emailSent === false) {
        alert(`Invite created but failed to send email: ${r.emailError || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Invite error', err);
      alert(err.response?.data?.error || 'Failed to create invite');
    } finally { setInviteLoading(false); }
  };

  const getStats = (id) => {
    const inv = invoices.filter(i => i.client === id || i.client?._id === id);
    return { count: inv.length, total: inv.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0) };
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700 }}>Clients ({clients.length})</h1>
        <button onClick={() => openModal()} style={btn}><Plus style={{ width: '1rem', height: '1rem' }} /> Add Client</button>
      </div>

      <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
        <Search style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: '#64748b' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." style={{ ...input, paddingLeft: '3rem' }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
        {filtered.map(client => {
          const stats = getStats(client._id);
          return (
            <div key={client._id} style={{ ...card, padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h3
                    style={{
                      fontWeight: 600,
                      fontSize: '1.02rem',
                      lineHeight: 1.25,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      marginBottom: '0.25rem'
                    }}
                  >
                    {client.name}
                  </h3>
                  <p
                    style={{
                      fontSize: '0.8rem',
                      color: '#94a3b8',
                      lineHeight: 1.25,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere'
                    }}
                  >
                    {client.company}
                  </p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 2.25rem)', gap: '0.5rem', flexShrink: 0 }}>
                  <button onClick={() => openModal(client)} style={actionBtn} onMouseEnter={e => e.currentTarget.style.background = 'rgba(148,163,184,0.2)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(148,163,184,0.1)'}><Edit style={{ width: '1.125rem', height: '1.125rem', display: 'block', flexShrink: 0 }} /></button>
                  <button onClick={() => handleDelete(client._id)} style={actionBtn} onMouseEnter={e => e.currentTarget.style.background = 'rgba(148,163,184,0.2)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(148,163,184,0.1)'}><Trash2 style={{ width: '1.125rem', height: '1.125rem', display: 'block', flexShrink: 0 }} /></button>
                  <button onClick={() => handleInviteClick(client)} title="Send invite" style={actionBtn} onMouseEnter={e => e.currentTarget.style.background = 'rgba(148,163,184,0.2)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(148,163,184,0.1)'}><Mail style={{ width: '1.125rem', height: '1.125rem', display: 'block', flexShrink: 0 }} /></button>
                </div>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}><Mail style={{ width: '1rem', height: '1rem' }} /> {client.email}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Phone style={{ width: '1rem', height: '1rem' }} /> {client.phone || 'N/A'}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ textAlign: 'center' }}><p style={{ fontSize: '1.125rem', fontWeight: 600 }}>{stats.count}</p><p style={{ fontSize: '0.75rem', color: '#64748b' }}>Invoices</p></div>
                <div style={{ textAlign: 'center' }}><p style={{ fontSize: '1.125rem', fontWeight: 600, color: '#34d399' }}>${stats.total.toLocaleString()}</p><p style={{ fontSize: '0.75rem', color: '#64748b' }}>Spent</p></div>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div style={{ width: '100%', maxWidth: '32rem', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{editing ? 'Edit' : 'Add'} Client</h2>
              <button onClick={() => setShowModal(false)} style={{ padding: '0.5rem', color: '#94a3b8' }}><X style={{ width: '1.25rem', height: '1.25rem' }} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[['Name *', 'name'], ['Email *', 'email'], ['Phone', 'phone'], ['Company', 'company'], ['Address', 'address']].map(([label, key]) => (
                <div key={key}><label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>{label}</label><input value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={input} /></div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.75rem', color: '#fff' }}>Cancel</button>
              <button onClick={handleSubmit} style={{ ...btn, flex: 1, justifyContent: 'center' }}>{editing ? 'Save' : 'Add Client'}</button>
            </div>
          </div>
        </div>
      )}

      {showInvite && inviteData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div style={{ width: '100%', maxWidth: '36rem', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Invite Token for {inviteData.client.name}</h2>
              <button onClick={() => { setShowInvite(false); setInviteData(null); }} style={{ padding: '0.5rem', color: '#94a3b8' }}><X style={{ width: '1.25rem', height: '1.25rem' }} /></button>
            </div>
            <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>Share this token with the client to accept their invite. They should open the admin-provided invite link or use the portal link with the token.</p>
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <div style={{ fontFamily: 'monospace', wordBreak: 'break-all', color: '#cbd5e1' }}>{inviteData.token}</div>
                <button onClick={() => { navigator.clipboard.writeText(inviteData.token); alert('Copied invite token'); }} style={{ padding: '0.5rem 0.75rem', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>Copy</button>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem' }}>Expires: {new Date(inviteData.expiresAt).toLocaleString()}</div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={() => { window.open(`https://www.pagehyve.com/portal?invite=${inviteData.token}&email=${encodeURIComponent(inviteData.client.email)}`, '_blank'); }} style={{ ...btn, justifyContent: 'center' }}>Open Portal Invite Link</button>
              <button onClick={() => { setShowInvite(false); setInviteData(null); }} style={{ flex: 1, padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.75rem', color: '#fff' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      <EmailPreviewModal 
        isOpen={showPreview}
        onClose={() => { setShowPreview(false); setPreviewClient(null); }}
        onConfirm={handleConfirmInvite}
        clientName={previewClient?.name}
        clientEmail={previewClient?.email}
        isLoading={inviteLoading}
      />
    </div>
  );
}
